import { SchoolYear } from "@/entities/schoolYear/schoolYear"
import { User } from "@/entities/user/user"
import { eachDayOfInterval, startOfDay, endOfDay, isWithinInterval, isWeekend } from "date-fns"

export interface ExportPeriod {
   from: Date
   to: Date
}

export type AttendanceStats = {
   present: number
   presentPercentage: number
   perfectPresent: number
   perfectPresentPercentage: number
   absentJustified: number
   absentJustifiedPercentage: number
   absentNotJustified: number
   absentNotJustifiedPercentage: number
   late: number
   latePercentage: number
   inSchoolDaysCount: number
}

function isDateInVacation(date: Date, vacationPeriods: { start_date: Date; end_date: Date }[]) {
   return vacationPeriods.some((period) =>
      isWithinInterval(date, { start: period.start_date, end: period.end_date }),
   )
}

const min1 = (val: number) => (val === 0 ? 0 : Math.ceil(Math.max(val, 1)))

export async function _computeStatsForYoungs(
   youngs: User[],
   period: ExportPeriod,
   currentSchoolYear: SchoolYear,
   computeAttendanceFn: any,
): Promise<{
   stats: (AttendanceStats & { young: User; period: ExportPeriod })[]
   globalStats: AttendanceStats
}> {
   const stats: (AttendanceStats & { young: User; period: ExportPeriod })[] = []

   // For each day in the period
   const days = eachDayOfInterval({ start: period.from, end: period.to })

   // Filter out vacation days & weekends
   const workingDays = days.filter(
      (day) => !isDateInVacation(day, currentSchoolYear.vacation_periods || []) && !isWeekend(day),
   )

   for (const young of youngs) {
      let present = 0
      let absentJustified = 0
      let absentNotJustified = 0
      let late = 0
      let inSchoolDaysCount = 0

      for (const day of workingDays) {
         const attendance = await computeAttendanceFn({
            young_id: young.id,
            schoolyear_id: currentSchoolYear.id,
            from: startOfDay(day),
            to: endOfDay(day),
         })

         if (attendance.status === "external") {
            continue
         }

         inSchoolDaysCount++

         if (attendance.status === "present") {
            present++
         } else if (attendance.status === "absent") {
            if (attendance.justified) {
               absentJustified++
            } else {
               absentNotJustified++
            }
         } else if (attendance.status === "late") {
            present++
            late++
         }
      }

      let perfectPresent = present - late
      // Calculate raw percentages
      let presentPercentage = Math.round(min1((present / inSchoolDaysCount) * 100))
      let perfectPresentPercentage = Math.round(min1((perfectPresent / inSchoolDaysCount) * 100))
      let absentJustifiedPercentage = Math.round(min1((absentJustified / inSchoolDaysCount) * 100))
      let absentNotJustifiedPercentage = Math.round(
         min1((absentNotJustified / inSchoolDaysCount) * 100),
      )
      let latePercentage = Math.round(min1((late / present) * 100))

      // Adjust percentages to ensure they sum to 100 (for presence/absence)
      const sum = presentPercentage + absentJustifiedPercentage + absentNotJustifiedPercentage
      if (sum > 100) {
         const excess = sum - 100
         // Remove excess proportionally from the largest values
         const values = [
            { name: "present", value: presentPercentage },
            { name: "justified", value: absentJustifiedPercentage },
            { name: "notJustified", value: absentNotJustifiedPercentage },
         ].sort((a, b) => b.value - a.value)

         for (let i = 0; i < excess; i++) {
            if (values[0].name === "present") presentPercentage--
            else if (values[0].name === "justified") absentJustifiedPercentage--
            else absentNotJustifiedPercentage--
         }
      }

      perfectPresentPercentage = Math.min(perfectPresentPercentage, presentPercentage)

      stats.push({
         young,
         period,
         present,
         presentPercentage,
         perfectPresent,
         perfectPresentPercentage,
         absentJustified,
         absentJustifiedPercentage,
         absentNotJustified,
         absentNotJustifiedPercentage,
         late,
         latePercentage,
         inSchoolDaysCount,
      })
   }

   // Calculate global stats by reducing individual stats
   const globalStats = stats.reduce(
      (acc, curr) => ({
         present: acc.present + curr.present,
         perfectPresent: acc.perfectPresent + curr.perfectPresent,
         absentJustified: acc.absentJustified + curr.absentJustified,
         absentNotJustified: acc.absentNotJustified + curr.absentNotJustified,
         late: acc.late + curr.late,
         inSchoolDaysCount: acc.inSchoolDaysCount + curr.inSchoolDaysCount,
         presentPercentage: 0, // Will be calculated below
         perfectPresentPercentage: 0,
         absentJustifiedPercentage: 0,
         absentNotJustifiedPercentage: 0,
         latePercentage: 0,
         inSchoolDaysCountAverage: 0,
      }),
      {
         present: 0,
         perfectPresent: 0,
         absentJustified: 0,
         absentNotJustified: 0,
         late: 0,
         inSchoolDaysCount: 0,
         presentPercentage: 0,
         perfectPresentPercentage: 0,
         absentJustifiedPercentage: 0,
         absentNotJustifiedPercentage: 0,
         latePercentage: 0,
         inSchoolDaysCountAverage: 0,
      },
   )

   // Calculate global percentages
   let presentPercentage = Math.round(
      min1((globalStats.present / globalStats.inSchoolDaysCount) * 100),
   )
   let perfectPresentPercentage = Math.round(
      min1((globalStats.perfectPresent / globalStats.inSchoolDaysCount) * 100),
   )
   let absentJustifiedPercentage = Math.round(
      min1((globalStats.absentJustified / globalStats.inSchoolDaysCount) * 100),
   )
   let absentNotJustifiedPercentage = Math.round(
      min1((globalStats.absentNotJustified / globalStats.inSchoolDaysCount) * 100),
   )
   let latePercentage = Math.round(min1((globalStats.late / globalStats.present) * 100))

   // Adjust global percentages to ensure they sum to 100
   const globalSum = presentPercentage + absentJustifiedPercentage + absentNotJustifiedPercentage
   if (globalSum > 100) {
      const excess = globalSum - 100
      const values = [
         { name: "present", value: presentPercentage },
         { name: "justified", value: absentJustifiedPercentage },
         { name: "notJustified", value: absentNotJustifiedPercentage },
      ].sort((a, b) => b.value - a.value)

      for (let i = 0; i < excess; i++) {
         if (values[0].name === "present") presentPercentage--
         else if (values[0].name === "justified") absentJustifiedPercentage--
         else absentNotJustifiedPercentage--
      }
   }

   perfectPresentPercentage = Math.min(perfectPresentPercentage, presentPercentage)

   globalStats.presentPercentage = presentPercentage
   globalStats.perfectPresentPercentage = perfectPresentPercentage
   globalStats.absentJustifiedPercentage = absentJustifiedPercentage
   globalStats.absentNotJustifiedPercentage = absentNotJustifiedPercentage
   globalStats.latePercentage = latePercentage
   globalStats.inSchoolDaysCountAverage = globalStats.inSchoolDaysCount / stats.length

   return { stats, globalStats }
}
